// iOS-style 3-column wheel picker for Date of Birth (Day · Month · Year).
//
// Design notes:
//  - Pure CSS scroll-snap; no external deps.
//  - Each wheel is a scrollable column where the centred row is the selected
//    value. Top/bottom padding equal to (visibleRows-1)/2 * rowHeight reveals
//    earlier/later items on either side of the highlighted band.
//  - On scroll-end we round to the nearest snap position and emit the value.
//  - Friction-rich on touch: relies on native momentum + `scroll-snap-stop:
//    always` for smooth wheel feel.
//  - Locale-aware month names via Intl.DateTimeFormat.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ROW_H = 40;          // px per row
const VISIBLE_ROWS = 5;    // odd; centre is selected

// Pad rows that line the wheel above/below the highlighted band, so that
// every real value can scroll into the centre.
const PAD_ROWS = (VISIBLE_ROWS - 1) / 2;

function range(from, to) {
    const out = [];
    if (from <= to) for (let i = from; i <= to; i++) out.push(i);
    else for (let i = from; i >= to; i--) out.push(i);
    return out;
}

function daysInMonth(year, month1Based) {
    return new Date(year, month1Based, 0).getDate();
}

function monthLabels(locale) {
    const fmt = new Intl.DateTimeFormat(locale || undefined, { month: "long" });
    return range(1, 12).map((m) => {
        const label = fmt.format(new Date(2020, m - 1, 1));
        return label.charAt(0).toUpperCase() + label.slice(1);
    });
}

function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
}

/**
 * Single scroll wheel column.
 *   items:        list of strings to render (length matches `values`).
 *   valueIndex:   currently selected index.
 *   onChange:     called with new index after the wheel settles.
 */
function Wheel({ items, valueIndex, onChange, testId, ariaLabel }) {
    const ref = useRef(null);
    const settleTimer = useRef(null);
    const isProgrammatic = useRef(false);

    // Sync external value -> scroll position.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const target = valueIndex * ROW_H;
        if (Math.abs(el.scrollTop - target) > 1) {
            isProgrammatic.current = true;
            el.scrollTop = target;
            // Allow the scroll handler to ignore this jump.
            setTimeout(() => { isProgrammatic.current = false; }, 50);
        }
    }, [valueIndex, items.length]);

    const handleScroll = useCallback(() => {
        if (isProgrammatic.current) return;
        if (settleTimer.current) clearTimeout(settleTimer.current);
        settleTimer.current = setTimeout(() => {
            const el = ref.current;
            if (!el) return;
            const idx = clamp(Math.round(el.scrollTop / ROW_H), 0, items.length - 1);
            // Snap (pixel-perfect) — scroll-snap may leave us off by 1 px.
            const target = idx * ROW_H;
            if (Math.abs(el.scrollTop - target) > 0.5) {
                el.scrollTo({ top: target, behavior: "smooth" });
            }
            if (idx !== valueIndex) onChange(idx);
        }, 120);
    }, [items.length, onChange, valueIndex]);

    return (
        <div
            ref={ref}
            data-testid={testId}
            aria-label={ariaLabel}
            role="listbox"
            onScroll={handleScroll}
            className="dob-wheel relative flex-1 overflow-y-auto no-scrollbar"
            style={{
                height: ROW_H * VISIBLE_ROWS,
                scrollSnapType: "y mandatory",
                WebkitMaskImage:
                    "linear-gradient(180deg, rgba(0,0,0,0) 0, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)",
                maskImage:
                    "linear-gradient(180deg, rgba(0,0,0,0) 0, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)",
                scrollBehavior: "smooth",
                touchAction: "pan-y",
            }}
        >
            <div style={{ height: ROW_H * PAD_ROWS }} aria-hidden />
            {items.map((label, i) => {
                const dist = Math.abs(i - valueIndex);
                const opacity = dist === 0 ? 1 : dist === 1 ? 0.5 : 0.22;
                const scale = dist === 0 ? 1 : 0.92;
                return (
                    <div
                        key={i}
                        role="option"
                        aria-selected={i === valueIndex}
                        style={{
                            height: ROW_H,
                            scrollSnapAlign: "center",
                            scrollSnapStop: "always",
                            opacity,
                            transform: `scale(${scale})`,
                            transition: "opacity 140ms ease, transform 140ms ease",
                        }}
                        className="flex items-center justify-center font-data text-[17px] tabular-nums tracking-tight"
                    >
                        {label}
                    </div>
                );
            })}
            <div style={{ height: ROW_H * PAD_ROWS }} aria-hidden />
        </div>
    );
}

/**
 * Three-wheel DOB picker.
 *   value:    "YYYY-MM-DD" | null
 *   onChange: (newIso) => void  — called whenever any wheel settles
 *   minAge / maxAge: bounds (default 13 / 100)
 */
export default function DobWheelPicker({
    value,
    onChange,
    minAge = 13,
    maxAge = 100,
    locale,
}) {
    const today = useMemo(() => new Date(), []);
    const minYear = today.getFullYear() - maxAge;
    const maxYear = today.getFullYear() - minAge;

    // Default = 30 years ago, Jan 1, if no value provided.
    const initial = useMemo(() => {
        if (value) {
            const [y, m, d] = value.split("-").map(Number);
            if (y && m && d) return { y, m, d };
        }
        const y = clamp(today.getFullYear() - 30, minYear, maxYear);
        return { y, m: 1, d: 1 };
    }, [value, today, minYear, maxYear]);

    const [year, setYear] = useState(initial.y);
    const [month, setMonth] = useState(initial.m);
    const [day, setDay] = useState(initial.d);

    // Re-sync from external `value` prop only when it actually changes string.
    useEffect(() => {
        if (!value) return;
        const [y, m, d] = value.split("-").map(Number);
        if (y && m && d && (y !== year || m !== month || d !== day)) {
            setYear(y); setMonth(m); setDay(d);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const years = useMemo(() => range(maxYear, minYear), [minYear, maxYear]);   // newest first → matches iOS
    const months = useMemo(() => monthLabels(locale), [locale]);
    const days = useMemo(() => range(1, daysInMonth(year, month)), [year, month]);

    // If month/year change clips current day, clamp.
    useEffect(() => {
        if (day > days.length) setDay(days.length);
    }, [days.length, day]);

    // Emit changes upward.
    useEffect(() => {
        const iso = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        onChange?.(iso);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, month, day]);

    return (
        <div className="relative w-full select-none" data-testid="dob-wheel-picker">
            {/* Highlighted centre band */}
            <div
                aria-hidden
                className="absolute left-2 right-2 top-1/2 -translate-y-1/2 rounded-xl bg-secondary/60 border-y border-foreground/10 pointer-events-none"
                style={{ height: ROW_H }}
            />
            <div className="relative flex gap-2">
                <Wheel
                    items={days.map(String)}
                    valueIndex={day - 1}
                    onChange={(i) => setDay(i + 1)}
                    testId="dob-wheel-day"
                    ariaLabel="Day"
                />
                <Wheel
                    items={months}
                    valueIndex={month - 1}
                    onChange={(i) => setMonth(i + 1)}
                    testId="dob-wheel-month"
                    ariaLabel="Month"
                />
                <Wheel
                    items={years.map(String)}
                    valueIndex={years.indexOf(year)}
                    onChange={(i) => setYear(years[i])}
                    testId="dob-wheel-year"
                    ariaLabel="Year"
                />
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { scrollbar-width: none; }
            `}</style>
        </div>
    );
}

/** Compute age in full years from an ISO date. */
export function ageFromIso(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return null;
    const today = new Date();
    let age = today.getFullYear() - y;
    if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) {
        age -= 1;
    }
    return Math.max(0, age);
}
